import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Text, Box, TextArea } from '@rsos/base-components';
import { Select } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import { getTokens } from '@rsos/sinatra';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import { selectTranscriptionForCall } from '../transcriptionSlice';
import {
  CustomModalHeader,
  HeaderContent,
  CloseButton,
} from './TranscriptFeedback.styles';
import { FEEDBACK_CATEGORIES, FORM_VALIDATION, UI_TEXT } from './constants';
import { useSubmitFeedbackMutation } from './transcriptFeedbackApi';
import type {
  TranscriptFeedbackModalProps,
  FeedbackFormData,
  SelectOption,
  RootState,
  ApiError,
} from './types';
import { getCallMetadata, getWindowData, validateFormData } from './utils';

const TranscriptFeedbackModal: React.FC<TranscriptFeedbackModalProps> = ({
  isOpen,
  onClose,
  callerId,
}) => {
  const [formData, setFormData] = useState<FeedbackFormData>({
    category: '',
    freeformText: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitFeedback] = useSubmitFeedbackMutation();

  const callQueueData = useSelector(
    (state: RootState) => state.emergencyCalls.callQueue[callerId],
  );

  const orgName = useSelector(
    (state: RootState) => state.sinatra?.user?.currentOrg?.name,
  );

  const currentPSAP = useSelector(
    (state: RootState) => state.psaps?.currentPSAP,
  );

  const transcriptionData = useSelector(selectTranscriptionForCall(callerId));

  const selectedCategory = FEEDBACK_CATEGORIES.find(
    category => category.value === formData.category,
  );

  const { token } = getTokens() ?? { token: null };

  if (!token && isOpen) {
    // eslint-disable-next-line -- because this is a good thing to report to sentry and shouldn't fail silently.
    console.error('No authentication token available for feedback submission');
    return null;
  }

  const handleCategoryChange = (selectedOption: SelectOption | null) => {
    setFormData(prev => ({
      ...prev,
      category: selectedOption?.value || '',
    }));
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      freeformText: event.target.value,
    }));
  };

  const resetForm = () => {
    setFormData({
      category: '',
      freeformText: '',
    });
  };

  const handleSubmit = async () => {
    if (!isOpen) {
      // eslint-disable-next-line -- helpful for developers to understand why their feedback submission isn't working?
      console.warn('Attempted to submit feedback while modal is closed');
      return;
    }

    if (!validateFormData(formData)) return;

    setIsSubmitting(true);

    try {
      const callMetadata = getCallMetadata(
        callQueueData,
        transcriptionData,
        callerId,
      );
      const windowData = getWindowData();

      // Extract authority data from Redux state
      const authorityData = currentPSAP
        ? {
            authorityId: currentPSAP.id,
            authorityDisplayName: currentPSAP.display_name,
            authorityAccountId: currentPSAP.account_id,
          }
        : undefined;

      await submitFeedback({
        formData,
        callMetadata,
        windowData,
        callerId,
        authorityData,
        orgName: orgName || 'default',
      }).unwrap();

      onClose();
      resetForm();
      notifySuccess(UI_TEXT.SUCCESS_MESSAGE);
    } catch (error) {
      const apiError = error as ApiError;
      notifyError(
        `Failed to submit feedback: ${apiError?.data?.message || apiError?.message || 'Unknown error'}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpened={isOpen}
      onClose={handleClose}
      width={500}
      padding="0px"
      id="transcript-feedback-modal"
      data-name="transcript-feedback-modal"
    >
      <CustomModalHeader>
        <HeaderContent>
          <Text size="xlarge" color="background">
            {UI_TEXT.MODAL_TITLE}
          </Text>
          <Text size="small" color="background" marginTop="4px">
            {UI_TEXT.MODAL_SUBTITLE}
          </Text>
        </HeaderContent>
        <CloseButton onClick={handleClose}>
          <Text>✕</Text>
        </CloseButton>
      </CustomModalHeader>

      <ModalBody>
        <Box flow={20} padding="20px">
          <Box>
            <Text size="normal" color="primaryText" marginBottom="8px">
              {UI_TEXT.CATEGORY_LABEL}
            </Text>
            <Select
              options={FEEDBACK_CATEGORIES}
              value={selectedCategory}
              onChange={handleCategoryChange}
              placeholder={UI_TEXT.CATEGORY_PLACEHOLDER}
              isClearable={false}
              isSearchable={false}
            />
          </Box>

          <Box>
            <Text size="normal" color="primaryText" marginBottom="8px">
              {UI_TEXT.FREEFORM_LABEL}
            </Text>
            <TextArea
              value={formData.freeformText}
              onChange={handleTextChange}
              placeholder={UI_TEXT.FREEFORM_PLACEHOLDER}
              rows={4}
            />
            <Text size="small" color="secondaryText" marginTop="4px">
              {formData.freeformText.length}/{FORM_VALIDATION.MAX_TEXT_LENGTH}{' '}
              characters
            </Text>
          </Box>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box horizontal justify="flex-end" gridColumnGap="12px" padding="20px">
          <Button
            btnType="tertiary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {UI_TEXT.CANCEL_BUTTON}
          </Button>
          <Button
            btnType="primary"
            onClick={handleSubmit}
            disabled={!formData.category || isSubmitting}
          >
            {isSubmitting ? 'Sending...' : UI_TEXT.SUBMIT_BUTTON}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default TranscriptFeedbackModal;
